/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import Component from '@glimmer/component';
import { isBlank } from '@ember/utils';
import { tracked } from '@glimmer/tracking';

interface CioWysihtml5LinkSignature {
  Args: {
    disabled: boolean;
  };
  Element: HTMLDivElement;
}

export default class CioWysihtml5Link extends Component<CioWysihtml5LinkSignature> {
  @tracked linkUrl = null;
  @tracked linkText = null;
  @tracked showModal = false;

  @tracked errors: { url: { message: null | string } } = {
    url: {
      message: null,
    },
  };

  @computed('errors.url.message')
  get hasError() {
    if (this.errors.url.message !== null) {
      return true;
    }

    return false;
  }

  #populateLink() {
    let editor = window.wysihtml5EditorInstance;
    let link = editor.composer.commands.state('createLink');
    let range = editor.composer.selection.getRange();

    this.linkUrl = null;
    this.linkText = null;

    let href = link?.[0]?.getAttribute('href');

    // Use href of selected link if exists
    if (link?.[0] !== null && href === null) {
      this.linkUrl = href;
    }

    // Use selected range text if exists
    if (range !== null && range.toString() !== '') {
      this.linkText = range.toString();
      return;
    }

    // Otherwise, use the selected link text if exists
    if (link?.[0]?.text !== null) {
      this.linkText = link[0].text;
    }
  }

  #focusEditor() {
    return window.wysihtml5EditorInstance.currentView.element.focus();
  }

  @action
  toggleModal() {
    this.showModal = !this.showModal;

    if (this.showModal) {
      this.#populateLink();
    } else {
      this.#focusEditor();
    }
  }

  @action
  insertLink() {
    let editor = window.wysihtml5EditorInstance;
    let { linkUrl, linkText } = this;

    if (isBlank(linkUrl)) {
      editor.composer.commands.exec('removeLink');
    } else {
      this.#validateUrl();
      if (this.hasError) {
        return;
      }
      editor.composer.commands.exec('createLink', {
        href: linkUrl,
        target: '_blank',
        text: linkText,
      });
    }

    this.linkUrl = null;
    this.linkText = null;
    this.showModal = false;
  }

  #linkIsValid(linkUrl: string) {
    let isValid = true;
    try {
      new URL(linkUrl);
    } catch {
      isValid = false;
    }
    return isValid;
  }

  #validateUrl() {
    let { linkUrl } = this;

    if (linkUrl === null || linkUrl === '') {
      return;
    }

    if (this.#linkIsValid(linkUrl)) {
      this.errors = {
        url: {
          message: null,
        },
      };
      return;
    }

    this.errors = { ...this.errors, url: { message: 'Invalid URL, must contain http:// or https://' } };
  }

  @action
  cancelLink() {
    this.linkText = null;
    this.linkUrl = null;
    this.showModal = false;
    this.errors = {
      url: {
        message: null,
      },
    };
    this.#focusEditor();
  }
}
